import type { PropsWithChildren, ReactElement } from 'react';

import LinkifyIt from 'linkify-it';
import tlds from 'tlds';

import styles from './linkify.module.css';

type Props = PropsWithChildren;

import { MENTION_REGEX } from '../../../../../utils/constants/mentions';

const linkify = new LinkifyIt({});
// Set top-level domains list and disable parsing 'ftp:', '//', 'mailto:' schemas.
linkify.tlds(tlds).add('ftp:', null).add('//', null).add('mailto:', null);

function isValidUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
}

function sanitizeUrl(url: string): string {
  // Ensure the URL starts with http/https
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

type Match = {
  start: number;
  end: number;
  type: 'mention' | 'url';
  text: string;
  url?: string;
};

function getAllMatches(text: string): Match[] {
  const matches: Match[] = [];

  // Get mention matches
  const mentionMatches = Array.from(text.matchAll(MENTION_REGEX));
  mentionMatches.forEach((match) => {
    if (match.index !== undefined) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'mention',
        text: match[0],
      });
    }
  });

  // Get URL matches from linkify
  const urlMatches = linkify.match(text) ?? [];
  urlMatches.forEach((match) => {
    matches.push({
      start: match.index,
      end: match.lastIndex,
      type: 'url',
      text: match.raw,
      url: match.url,
    });
  });

  // Sort by start position and remove overlaps (mentions take priority)
  matches.sort((a, b) => a.start - b.start);

  const filteredMatches: Match[] = [];
  for (const match of matches) {
    const hasOverlap = filteredMatches.some(
      (existing) =>
        (match.start >= existing.start && match.start < existing.end) ||
        (match.end > existing.start && match.end <= existing.end) ||
        (match.start <= existing.start && match.end >= existing.end),
    );

    if (!hasOverlap) {
      filteredMatches.push(match);
    }
  }

  return filteredMatches;
}

export function Linkify(props: Props) {
  if (typeof props.children !== 'string') {
    return props.children;
  }

  const matches = getAllMatches(props.children);

  if (matches.length === 0) {
    return <span className={styles.wrapper}>{props.children}</span>;
  }

  const parsedValue: (string | ReactElement)[] = [];
  let lastIndex = 0;

  matches.forEach((match, _index) => {
    // Push plain text before this match
    if (match.start > lastIndex) {
      parsedValue.push(String(props.children).slice(lastIndex, match.start));
    }

    if (match.type === 'mention') {
      const label = match.text.split('[')[0];
      parsedValue.push(
        <span className={styles.mention_item} key={`m-${match.start}`}>
          {label}
        </span>,
      );
    } else if (match.type === 'url') {
      const sanitizedUrl = sanitizeUrl(match.url || match.text);

      if (isValidUrl(sanitizedUrl)) {
        parsedValue.push(
          <a
            href={sanitizedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
            key={`l-${match.start}`}
          >
            {match.text}
          </a>,
        );
      } else {
        parsedValue.push(match.text);
      }
    }

    lastIndex = match.end;
  });

  // Add remaining text
  if (lastIndex < props.children.length) {
    parsedValue.push(props.children.slice(lastIndex));
  }

  return <span className={styles.wrapper}>{parsedValue}</span>;
}
